import { format } from "date-fns";
import { AlertTriangle, PauseCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import type { RateLimitSummary } from "@/lib/github/rate-limit";

// Banner handles the *persistent* low-budget signal (remaining < 200 and < 20).
// The *transient* critical signal (first crossing of < 20) is handled by a toast
// in rate-limit.ts — these two mechanisms target different UX needs and do not
// duplicate each other.

interface RateLimitBannerProps {
  summary: RateLimitSummary;
  onRetry?: () => void;
}

export function RateLimitBanner({ summary, onRetry }: RateLimitBannerProps) {
  if (summary.exhausted) {
    const resetAt = format(summary.resetAt, "HH:mm");
    return (
      <Alert variant="destructive">
        <PauseCircle className="size-4" />
        <AlertTitle>GitHub rate limit nearly exhausted</AlertTitle>
        <AlertDescription className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
          <span>
            Only {summary.remaining} / {summary.limit} requests left. Resets at {resetAt}.
          </span>
          {onRetry ? (
            <Button size="sm" variant="outline" onClick={onRetry}>
              Retry
            </Button>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  }

  if (summary.lowBudget) {
    return (
      <Alert>
        <AlertTriangle className="size-4" />
        <AlertTitle>Low API budget</AlertTitle>
        <AlertDescription>
          {summary.remaining} / {summary.limit} requests remaining. Heavy fetches
          are paused below 20.
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}
